import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Badge } from "./badge";
import { 
  X, 
  FileText, 
  Download, 
  ExternalLink,
  Package,
  DollarSign,
  Hash,
  Info,
  Calendar,
  User,
  Building,
  ChevronLeft,
  ChevronRight,
  Eye
} from "lucide-react";

export const DocumentModal = ({ 
  isOpen, 
  onClose, 
  documents, // Now expecting an array of documents
  item, 
  onDownload 
}) => {
  // Hooks must be called before any early returns
  const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);

  // Reset current document index when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setCurrentDocumentIndex(0);
    }
  }, [isOpen]);

  // Early return after hooks
  if (!documents || !documents.length || !item) return null;

  const currentDocument = documents[currentDocumentIndex];
  const { fileUrl, filename } = currentDocument;

  // Format currency values
  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Format field names
  const formatFieldName = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get file extension
  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || '';
  };

  const fileExtension = getFileExtension(filename);
  const isPDF = fileExtension === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);

  // Navigation functions
  const goToNextDocument = () => {
    setCurrentDocumentIndex((prev) => (prev + 1) % documents.length);
  };

  const goToPreviousDocument = () => {
    setCurrentDocumentIndex((prev) => (prev - 1 + documents.length) % documents.length);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[95vw] h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DialogTitle className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {filename || 'Document Viewer'}
              </DialogTitle>
              {documents.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {currentDocumentIndex + 1} of {documents.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousDocument}
                      disabled={documents.length <= 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextDocument}
                      disabled={documents.length <= 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(fileUrl, '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
              {onDownload && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(fileUrl, filename)}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex h-full">
          {/* Document Viewer - Left Side */}
          <div className="flex-1 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700">
            <div className="h-full p-4">
              {isPDF ? (
                <iframe
                  src={fileUrl}
                  className="w-full h-full rounded border border-slate-200 dark:border-slate-700 bg-white"
                  title={filename}
                />
              ) : isImage ? (
                <div className="h-full flex items-center justify-center">
                  <img
                    src={fileUrl}
                    alt={filename}
                    className="max-w-full max-h-full object-contain rounded border border-slate-200 dark:border-slate-700"
                  />
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                  <FileText className="h-16 w-16 text-slate-400 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-300 mb-2">
                    Preview Not Available
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-4">
                    This file type cannot be previewed in the browser.
                  </p>
                  <Button
                    onClick={() => window.open(fileUrl, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open File
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Item Information - Right Side */}
          <div className="w-96 bg-white dark:bg-slate-800 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Header with item name */}
              <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                    <Package className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {item.name || 'Item Details'}
                    </h2>
                    {item.item_code && (
                      <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                        <Hash className="h-3 w-3" />
                        <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded text-xs">
                          {item.item_code}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Key Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Key Information
                </h3>
                
                <div className="grid gap-3">
                  {item.description && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded p-3">
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Description
                      </label>
                      <p className="text-sm text-slate-800 dark:text-slate-200 mt-1">
                        {item.description}
                      </p>
                    </div>
                  )}

                  {(item.quantity || item.quantity_to_order) && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded p-3">
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Quantity
                      </label>
                      <p className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-1">
                        {item.quantity || item.quantity_to_order}
                        {item.unit && <span className="text-sm text-slate-500 ml-1">{item.unit}</span>}
                      </p>
                    </div>
                  )}

                  {(item.unit_price || item.total_cost || item.price) && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded p-3 border border-green-200 dark:border-green-700">
                      <label className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Pricing
                      </label>
                      <div className="mt-2 space-y-1">
                        {item.unit_price && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-700 dark:text-green-300">Unit Price:</span>
                            <span className="font-semibold text-green-800 dark:text-green-200">
                              {formatCurrency(item.unit_price)}
                            </span>
                          </div>
                        )}
                        {item.total_cost && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-700 dark:text-green-300">Total Cost:</span>
                            <span className="font-semibold text-green-800 dark:text-green-200">
                              {formatCurrency(item.total_cost)}
                            </span>
                          </div>
                        )}
                        {item.price && !item.unit_price && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-700 dark:text-green-300">Price:</span>
                            <span className="font-semibold text-green-800 dark:text-green-200">
                              {formatCurrency(item.price)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {item.criticality && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded p-3">
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Criticality
                      </label>
                      <div className="mt-1">
                        <Badge 
                          className={`
                            ${item.criticality.toLowerCase() === 'high' ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700' :
                              item.criticality.toLowerCase() === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700' :
                              'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'}
                          `}
                        >
                          {item.criticality}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  Additional Details
                </h3>
                
                <div className="space-y-2">
                  {Object.entries(item)
                    .filter(([key, value]) => 
                      value !== null && 
                      value !== undefined && 
                      value !== '' &&
                      !['name', 'description', 'quantity', 'quantity_to_order', 'unit_price', 'total_cost', 'price', 'criticality', 'item_code', 'unit', 'file_url', 'file_filename'].includes(key)
                    )
                    .map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                        <span className="text-sm text-slate-600 dark:text-slate-400 capitalize">
                          {formatFieldName(key)}:
                        </span>
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200 text-right max-w-[60%] break-words">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Document Information */}
              <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  Document Information
                </h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Current Document:
                    </span>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200 text-right max-w-[60%] break-words">
                      {currentDocumentIndex + 1} of {documents.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Filename:
                    </span>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200 text-right max-w-[60%] break-words">
                      {filename}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      File Type:
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {fileExtension.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* All Documents List */}
                {documents.length > 1 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
                      All Documents
                    </h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {documents.map((doc, index) => (
                        <div
                          key={index}
                          onClick={() => setCurrentDocumentIndex(index)}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            index === currentDocumentIndex
                              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                        >
                          <Eye className="h-3 w-3 text-slate-400" />
                          <span className="text-xs text-slate-600 dark:text-slate-300 truncate flex-1">
                            {doc.filename}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {getFileExtension(doc.filename).toUpperCase()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 